import { useState } from "react";
import { Link } from "react-router-dom";

import {
  useAssignUnassignedPatients,
  usePrescriberCoverage,
} from "@/lib/api/clinicianHooks";
import type { PrescriberCoveragePatient } from "@/types/clinician";

const REASON_LABEL: Record<PrescriberCoveragePatient["reason"], string> = {
  no_assignment: "No assigned prescriber",
  inactive_prescriber: "Prescriber inactive",
  unlicensed: "Prescriber not licensed in state",
};

/**
 * Team screen: every patient with an order should have an active prescriber
 * licensed in their state. Lists anyone who doesn't, since their next
 * prescription has no one to write it, and routes unassigned patients the
 * way a new patient is routed. Shown only while routing is active.
 */
export function PrescriberCoverage({ enabled }: { enabled: boolean }) {
  const coverage = usePrescriberCoverage(enabled);
  const assign = useAssignUnassignedPatients();
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const data = coverage.data;

  if (!enabled || !data?.team_mode) return null;
  const unreachable = data.no_assignment + data.inactive_prescriber;
  const total = unreachable + data.unlicensed;

  if (total === 0) {
    return (
      <p className="rounded border border-line bg-raised px-14 py-10 type-body-sm text-success">
        Prescriber coverage: every patient with an order has an active
        prescriber licensed in their state.
      </p>
    );
  }

  // Small batches until a batch assigns nobody (the rest can't be routed).
  async function run() {
    let assigned = 0;
    let still = 0;
    let failed = 0;
    try {
      for (let batch = 0; batch < 100; batch += 1) {
        const result = await assign.mutateAsync();
        assigned += result.assigned;
        still = result.still_unassigned;
        failed = result.failed;
        setNotice(`Assigning… ${assigned} so far.`);
        if (result.assigned === 0 || result.remaining === 0) break;
      }
      setNotice(
        `${assigned} assigned through routing.` +
          (still
            ? ` ${still} couldn't be: nobody routable is licensed in their state (or it's unknown).`
            : "") +
          (failed ? ` ${failed} failed; see the logs.` : ""),
      );
    } catch {
      setNotice(
        `Assignment stopped after ${assigned}. Patients already assigned keep their prescriber; run it again to continue.`,
      );
    }
    setConfirming(false);
  }

  return (
    <section
      role="alert"
      aria-label="Prescriber coverage"
      className={`rounded-lg border p-16 space-y-12 ${
        unreachable ? "border-danger/40 bg-danger/5" : "border-warning/40 bg-warning-wash"
      }`}
    >
      <div>
        <h2 className="type-heading-md">
          {unreachable
            ? `${unreachable} patient${unreachable === 1 ? "" : "s"} without a prescriber`
            : `${data.unlicensed} patient${data.unlicensed === 1 ? "" : "s"} whose prescriber isn't licensed in their state`}
        </h2>
        <p className="mt-4 type-body-sm text-ink-muted max-w-[820px]">
          {data.no_assignment} with no assigned prescriber ·{" "}
          {data.inactive_prescriber} assigned to an inactive prescriber ·{" "}
          {data.unlicensed} not licensed in the patient&apos;s state. Their next
          prescription has no one to write it. Unassigned patients can be
          routed below; for the others use <b>Move patients</b> on the
          prescriber&apos;s row.
        </p>
      </div>
      <div className="max-h-[18rem] overflow-auto rounded border border-line bg-page">
        <table className="w-full type-body-sm">
          <thead className="sticky top-0 bg-page text-left text-ink-muted">
            <tr>
              <th className="px-10 py-6">Patient</th>
              <th className="px-10 py-6">Issue</th>
              <th className="px-10 py-6">State</th>
              <th className="px-10 py-6">Prescriber</th>
              <th className="px-10 py-6">Last order</th>
            </tr>
          </thead>
          <tbody>
            {data.patients.map((row) => (
              <tr key={row.patient_id} className="border-t border-line-subtle">
                <td className="px-10 py-6">
                  <Link
                    className="underline underline-offset-2"
                    to={`/clinician-portal/patients/${row.patient_id}`}
                  >
                    {row.patient_name ?? row.patient_id.slice(0, 8)}
                  </Link>
                </td>
                <td className="px-10 py-6">{REASON_LABEL[row.reason]}</td>
                <td className="px-10 py-6 tabular-nums">{row.ship_state ?? "Unknown"}</td>
                <td className="px-10 py-6">{row.prescriber_name ?? "—"}</td>
                <td className="px-10 py-6 tabular-nums">
                  {row.last_order_at ? row.last_order_at.slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > data.patients.length && (
        <p className="type-body-sm text-ink-muted">
          Showing the first {data.patients.length} of {total}.
        </p>
      )}
      {data.no_assignment > 0 && (
        <div className="flex flex-wrap items-center gap-8">
          {confirming ? (
            <>
              <button
                type="button"
                onClick={run}
                disabled={assign.isPending}
                className="rounded bg-ink px-12 py-7 type-label-sm text-page disabled:opacity-40"
              >
                {assign.isPending ? "Assigning…" : `Assign ${data.no_assignment} through routing`}
              </button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded border border-line px-10 py-6 type-label-sm hover:bg-page"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setNotice(null);
                setConfirming(true);
              }}
              className="rounded border border-ink px-10 py-6 type-label-sm hover:bg-page"
            >
              Assign unassigned patients through routing…
            </button>
          )}
        </div>
      )}
      {notice && (
        <p role="status" className="type-body-sm">
          {notice}
        </p>
      )}
    </section>
  );
}

/** Worklist strip for supervisors: patients without a prescriber are a
 *  team-wide problem, so it shows wherever a supervisor works. */
export function PrescriberCoverageNotice({ enabled }: { enabled: boolean }) {
  const coverage = usePrescriberCoverage(enabled);
  const data = coverage.data;
  if (!enabled || !data?.team_mode) return null;
  const unreachable = data.no_assignment + data.inactive_prescriber;
  if (unreachable === 0) return null;
  return (
    <p
      role="alert"
      className="rounded border border-danger/40 bg-danger/5 px-12 py-8 type-body-sm"
    >
      {unreachable} patient{unreachable === 1 ? " has" : "s have"} no active
      prescriber.{" "}
      <Link className="underline underline-offset-2" to="/clinician-portal/team">
        Review prescriber coverage
      </Link>
    </p>
  );
}
